import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUpdateUser } from '@/services/noteGoApi/noteGoApiComponents';
import useAlertStore from '@/stores/alertStore';
import { config } from '@/config';
import { useTagCounts } from '@/hooks/useTagCounts';
import { errorReporter } from '@/services/errorReportingService';
import type { TagGroupSuggestion } from '../types';
import { fetchAiGroupSuggestions } from '../services/smartTagsService';

export const useSmartTags = () => {
  const { userData, userToken, fetchAndSetUserData } = useAuthStore();
  const { mutateAsync: updateUser } = useUpdateUser();
  const { showAlert } = useAlertStore();
  // Scope to the user's OWN notes. Smart Tags merges/renames tags on the user's
  // own tag vocabulary (it writes back via updateUser), so counting tags from
  // notes shared *with* them was both wrong and twice the data to download.
  // Matches useManageTags, which is the other consumer of this vocabulary.
  const { tagCounts, isLoading: isLoadingCounts } = useTagCounts({ shared: false });

  const [tags, setTags] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<TagGroupSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasFetchedSuggestions, setHasFetchedSuggestions] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [mergingIndex, setMergingIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTag, setSelectedTag] = useState('');

  // Edit dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [newTagName, setNewTagName] = useState('');

  // AbortController ref for AI suggestions
  const abortControllerRef = useRef<AbortController | null>(null);

  // Sync tags from tagCounts
  useEffect(() => {
    if (!isLoadingCounts) {
      const tagNames = Object.keys(tagCounts).sort((a, b) => a.localeCompare(b));
      setTags(tagNames);
    }
  }, [tagCounts, isLoadingCounts]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleUpdateTags = useCallback(
    async (updatedTags: string[]) => {
      if (!userData || !userToken) return;

      await updateUser({
        pathParams: { userId: userData.id },
        body: { tags: updatedTags },
        headers: { authorization: `Bearer ${userToken}` },
      });
      fetchAndSetUserData({ force: true });
    },
    [userData, userToken, updateUser, fetchAndSetUserData],
  );

  const fetchSuggestions = useCallback(async () => {
    if (!userToken || tags.length === 0) return;

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoadingSuggestions(true);
    setHasFetchedSuggestions(false);

    try {
      const result = await fetchAiGroupSuggestions(userToken, tags, tagCounts);
      setSuggestions(result);
      setHasFetchedSuggestions(true);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to get AI suggestions';
      errorReporter.expected('Smart Tags: Failed to fetch AI suggestions', error, {
        feature: 'smartTags',
        operation: 'fetchSuggestions',
      });
      showAlert(errorMessage, 'error');
      setHasFetchedSuggestions(true);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [userToken, tags, tagCounts, showAlert]);

  const acceptSuggestion = useCallback(
    async (suggestion: TagGroupSuggestion, index: number) => {
      if (!userToken || !userData) return;

      setIsMerging(true);
      setMergingIndex(index);

      try {
        const response = await fetch(`${config.apiUrl}/tags/merge`, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${userToken}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            source_tag_ids: suggestion.source_tags,
            target_tag_name: suggestion.target_tag,
            target_tag_color: null,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error((errorData as Record<string, string>).detail || 'Merge failed');
        }

        const result = (await response.json()) as { updated_note_count: number };

        // Update local tags: remove source tags, add target if not present
        const updatedTags = tags.filter((tag) => !suggestion.source_tags.includes(tag));
        if (!updatedTags.includes(suggestion.target_tag)) {
          updatedTags.push(suggestion.target_tag);
        }
        setTags(updatedTags);

        // Sync with backend
        await handleUpdateTags(updatedTags);

        // Remove accepted suggestion and any stale ones whose source_tags overlap with merged tags
        const mergedTagSet = new Set(suggestion.source_tags);
        setSuggestions((prev) =>
          prev.filter((s, i) => {
            if (i === index) return false;
            return !s.source_tags.some((tag) => mergedTagSet.has(tag));
          }),
        );

        showAlert(
          `Tags merged successfully into "${suggestion.target_tag}". ${result.updated_note_count} notes updated.`,
          'success',
        );
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to merge tags. Please try again.';
        errorReporter.expected('Smart Tags: Failed to accept suggestion', error, {
          feature: 'smartTags',
          operation: 'acceptSuggestion',
        });
        showAlert(errorMessage, 'error');
      } finally {
        setIsMerging(false);
        setMergingIndex(null);
      }
    },
    [userToken, userData, tags, handleUpdateTags, showAlert],
  );

  const dismissSuggestion = useCallback((index: number) => {
    setSuggestions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Delete tag handlers
  const handleOpenDeleteDialog = useCallback((tag: string) => {
    setSelectedTag(tag);
    setOpenDeleteDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setOpenDeleteDialog(false);
  }, []);

  const handleDeleteTag = useCallback(async () => {
    if (!userToken) return;

    try {
      setIsLoading(true);

      const response = await fetch(`${config.apiUrl}/tags/merge`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${userToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          source_tag_ids: [selectedTag],
          target_tag_name: '',
          target_tag_color: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as Record<string, string>).detail || 'Delete failed');
      }

      const result = (await response.json()) as { updated_note_count: number };

      const updatedTags = tags.filter((tag) => tag !== selectedTag);
      setTags(updatedTags);
      await handleUpdateTags(updatedTags);

      showAlert(`Tag deleted successfully. ${result.updated_note_count} notes updated.`, 'success');
      handleCloseDeleteDialog();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error deleting tag. Please try again.';
      showAlert(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [userToken, selectedTag, tags, handleUpdateTags, showAlert, handleCloseDeleteDialog]);

  // Edit tag handlers
  const handleOpenEditDialog = useCallback((tag: string) => {
    setSelectedTag(tag);
    setNewTagName(tag);
    setOpenEditDialog(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditDialog(false);
    setNewTagName('');
  }, []);

  const handleEditTag = useCallback(async () => {
    if (!userToken) return;

    if (!newTagName.trim()) {
      showAlert('Tag name cannot be empty', 'error');
      return;
    }

    if (tags.includes(newTagName) && newTagName !== selectedTag) {
      showAlert('This tag already exists', 'error');
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(`${config.apiUrl}/tags/merge`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${userToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          source_tag_ids: [selectedTag],
          target_tag_name: newTagName,
          target_tag_color: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as Record<string, string>).detail || 'Update failed');
      }

      const result = (await response.json()) as { updated_note_count: number };

      const updatedTags = tags.map((tag) => (tag === selectedTag ? newTagName : tag));
      setTags(updatedTags);
      await handleUpdateTags(updatedTags);

      showAlert(`Tag updated successfully. ${result.updated_note_count} notes updated.`, 'success');
      handleCloseEditDialog();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error updating tag. Please try again.';
      showAlert(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [userToken, newTagName, selectedTag, tags, handleUpdateTags, showAlert, handleCloseEditDialog]);

  return {
    // Tag data
    tags,
    tagCounts,
    isLoadingCounts,
    isLoading,

    // AI suggestions
    suggestions,
    isLoadingSuggestions,
    hasFetchedSuggestions,
    isMerging,
    mergingIndex,

    // AI suggestion actions
    fetchSuggestions,
    acceptSuggestion,
    dismissSuggestion,

    // Delete dialog
    openDeleteDialog,
    selectedTag,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleDeleteTag,

    // Edit dialog
    openEditDialog,
    newTagName,
    setNewTagName,
    handleOpenEditDialog,
    handleCloseEditDialog,
    handleEditTag,
  };
};
